import {
  OrderItemSearch,
  OrderItemSearchResult,
  OrderItemSummaryResult,
  SkuReports,
  SkuReportsResult,
  SkuReportsSearch,
  MissingStockSearch,
  MissingStockResult,
} from "@/types/OrderItem";
import {
  UnAssignSkuSearch,
  UnAssignSkuSearchResult,
} from "@/types/UnAssignSku";
import { createApi } from "@reduxjs/toolkit/query/react";
import { marketPlaceBaseQueryWrapper } from "@/utils/rtkHelper";

export const OrderItemApi = createApi({
  reducerPath: "OrderItemApi",
  baseQuery: marketPlaceBaseQueryWrapper,
  tagTypes: ["orderItem"],
  keepUnusedDataFor: 0,
  endpoints: (builder) => ({
    getOrderItem: builder.query({
      query: () => `/flipkart-order`,
      providesTags: ["orderItem"],
    }),
    getDashboardCounts: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/dashboard-count/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTotalOrdersChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/total-order-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getChargeableAmtChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/chargeable-amounts/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getReturnStatusChartChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/return-status-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10SkuProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/top-10-sku-profit-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10SkuLossChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/top-10-sku-loss-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10SalesSkuChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/top-10-sales-sku/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10StateWiseCustomerReturnChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/top-10-customer-returns-state/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10SkuOfCustomerReturnChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/top-10-sku-of-customer-return/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10CityWiseSalesOrderChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/top-10-sales-order-city-wise/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10StateWiseSalesOrderChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/top-10-sales-order-state-wise/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10CustomerReturnChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/top-10-customer-return/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getRealTimeProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/real-time-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getUpcomingProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/upcoming-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getNetProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-order/net-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),

    getDropdownOptions: builder.query({
      query: () => `/flipkart-order/sku/sku-dropdown`,
      providesTags: ["orderItem"],
    }),
    searchFlipkartOrders: builder.query<OrderItemSearchResult, OrderItemSearch>(
      {
        query: (input) => ({
          url: `/flipkart-order/search`,
          method: "POST",
          body: { ...input, export: false },
        }),
        providesTags: ["orderItem"],
      }
    ),
    exportFlipkartOrders: builder.mutation<Blob, OrderItemSearch>({
      query: (input) => ({
        url: `/flipkart-order/search`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    skuReportsFlipkartOrders: builder.query<SkuReportsResult, SkuReportsSearch>(
      {
        query: (input) => ({
          url: `/flipkart-order/sku-report`,
          method: "POST",
          body: { ...input, export: false },
        }),
        providesTags: ["orderItem"],
      }
    ),
    exportSkuReports: builder.mutation<Blob, SkuReportsSearch>({
      query: (input) => ({
        url: `/flipkart-order/sku-report`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    summaryOrderItem: builder.query<OrderItemSummaryResult, OrderItemSearch>({
      query: (input) => ({
        url: `/flipkart-order/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["orderItem"],
    }),
    summarySkuReport: builder.query<SkuReports, SkuReportsSearch>({
      query: (input) => ({
        url: `/flipkart-order/sku-report/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["orderItem"],
    }),
    deleteTable: builder.mutation({
      query: () => ({
        url: `/flipkart-order/truncate-tables`,
        method: "POST",
      }),
      invalidatesTags: ["orderItem"],
    }),
    getUnAssignSku: builder.query<UnAssignSkuSearchResult, UnAssignSkuSearch>({
      query: (input) => ({
        url: `/flipkart-order/unassigned-skus`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["orderItem"],
    }),
    exportUnAssignSku: builder.mutation<Blob, UnAssignSkuSearch>({
      query: (input) => ({
        url: `/flipkart-order/unassigned-skus`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    getMissingStock: builder.query<MissingStockResult, MissingStockSearch>({
      query: (input) => ({
        url: `/flipkart-order/missing-stock`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["orderItem"],
    }),
    exportMissingStock: builder.mutation<Blob, MissingStockSearch>({
      query: (input) => ({
        url: `/flipkart-order/missing-stock`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    getMissingStockCount: builder.query<number, void>({
      query: () => `/flipkart-order/missing_stock_count`,
      providesTags: ["orderItem"],
    }),
    getUniqueUnAssignSkus: builder.query<string[], void>({
      query: () => `/flipkart-order/skus/unique-unassign-skus`,
      providesTags: ["orderItem"],
    }),
  }),
});

export const {
  useGetOrderItemQuery,
  useGetDashboardCountsQuery,
  useGetChargeableAmtChartQuery,
  useGetTop10SkuProfitChartQuery,
  useGetTop10SkuLossChartQuery,
  useGetReturnStatusChartChartQuery,
  useSearchFlipkartOrdersQuery,
  useLazySearchFlipkartOrdersQuery,
  useExportFlipkartOrdersMutation,
  useSummaryOrderItemQuery,
  useSkuReportsFlipkartOrdersQuery,
  useLazySkuReportsFlipkartOrdersQuery,
  useExportSkuReportsMutation,
  useSummarySkuReportQuery,
  useDeleteTableMutation,
  useGetUnAssignSkuQuery,
  useLazyGetUnAssignSkuQuery,
  useExportUnAssignSkuMutation,
  useGetTotalOrdersChartQuery,
  useGetTop10StateWiseCustomerReturnChartQuery,
  useGetTop10SalesSkuChartQuery,
  useGetTop10SkuOfCustomerReturnChartQuery,
  useGetTop10CityWiseSalesOrderChartQuery,
  useGetTop10StateWiseSalesOrderChartQuery,
  useGetTop10CustomerReturnChartQuery,
  useGetDropdownOptionsQuery,
  useGetNetProfitChartQuery,
  useGetRealTimeProfitChartQuery,
  useGetUpcomingProfitChartQuery,
  useGetMissingStockQuery,
  useExportMissingStockMutation,
  useGetMissingStockCountQuery,
  useGetUniqueUnAssignSkusQuery,
  useLazyGetUniqueUnAssignSkusQuery,
} = OrderItemApi;
